import {
  AimOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  FilterOutlined,
  IssuesCloseOutlined,
  LinkOutlined,
  MinusCircleOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import {
  Card,
  Collapse,
  Empty,
  Image,
  Pagination,
  Popconfirm,
  Radio,
  RadioChangeEvent,
  Skeleton,
  Statistic,
  Tabs,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import default_bank from '../../Assets/ic_default_bank.png';
import {
  getAllAccountsByFIP,
  getAllConsentsByFIU,
  getAllFIPByUser,
  getAllFIUByUser,
  getAllUserActivity,
  getUserSpecificActivity,
} from '../../Services/UserActivityServices/UserActivity';
import { logoBaseUrl } from '../../Utils/Constants/baseUrl';
import { ConsentStatusType } from '../../Utils/Constants/consentTypes';
import TimeFromNow from '../Common/Components/TimeFromNow';
import { activityDescription, activityTitle } from './constant';
import './index.css';
interface IUserActivityProps {
  isLoading: boolean;
}

const UserActivity: React.FC<IUserActivityProps> = ({ isLoading }) => {
  const { t } = useTranslation();
  const [userActivity, setUserActivity] = useState<any>({});
  const [pageSize, setPageSize] = useState(20);
  const [pageSizeRoot, setPageSizeRoot] = useState(20);
  const [page, setPage] = useState(1);
  const [pageRoot, setPageRoot] = useState(1);
  const [activeTab, setActiveTab] = useState('1');
  const [totalCount, setTotalCount] = useState(0);
  const [totalCountRoot, setTotalCountRoot] = useState(0);
  const [fiu, setFiu] = useState<any[]>([]);
  const [linkedFip, setLinkedFip] = useState<any[]>([]);
  const [selectedFiu, setSelectedFiu] = useState('');
  const [selectedLinkedFip, setSelectedLinkedFip] = useState('');
  const [consents, setConsents] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [entityId, setEntityId] = useState('');
  const [sort, setSort] = useState('DESC');

  const returnLocalSortedUserActivity = useCallback(
    (res: any) => {
      const localUserActivity: any = {};
      res.forEach((r: any) => {
        if (localUserActivity[moment(new Date(r.time)).format('Do MMM YYYY')]) {
          localUserActivity[
            moment(new Date(r.time)).format('Do MMM YYYY')
          ].push(r);
          localUserActivity[
            moment(new Date(r.time)).format('Do MMM YYYY')
          ].sort((a: any, b: any) => {
            if (a.time === b.time) {
              return a.time - b.time;
            }

            if (entityId.length > 0 || sort === 'DESC') {
              return a.time < b.time ? 1 : -1;
            } else {
              return b.time < a.time ? 1 : -1;
            }
          });
        } else {
          localUserActivity[moment(new Date(r.time)).format('Do MMM YYYY')] = [
            r,
          ];
        }
      });

      return localUserActivity;
    },
    [entityId, sort]
  );

  const getUserActivity = useCallback(() => {
    if (entityId) {
      getUserSpecificActivity(
        page - 1,
        pageSize,
        entityId,
        activeTab === '2' ? 'Consent' : 'Account'
      )
        .then((res) => res.data)
        .then((res) => {
          setUserActivity(returnLocalSortedUserActivity(res.content));
          setTotalCount(res.totalElements);
        });
    } else {
      if (activeTab === '2') {
        if (fiu.length === 0) getAllFIUByUser().then((res) => setFiu(res.data));
      } else if (activeTab === '3') {
        if (linkedFip.length === 0)
          getAllFIPByUser().then((res) => setLinkedFip(res.data));
      } else {
        getAllUserActivity(
          page - 1,
          pageSize,
          activeTab === '2' ? '/consent' : activeTab === '3' ? '/account' : '',
          sort
        )
          .then((res) => res.data)
          .then((res) => {
            setUserActivity(returnLocalSortedUserActivity(res.content));
            setTotalCount(res.totalElements);
          });
      }
    }
  }, [
    page,
    pageSize,
    activeTab,
    entityId,
    fiu.length,
    linkedFip.length,
    returnLocalSortedUserActivity,
    sort,
  ]);

  useEffect(() => {
    getUserActivity();
  }, [getUserActivity]);

  const retrieveAuditBasedOnKey = (key: string) => {
    setActiveTab(key);
    if (page !== 1) {
      setPage(1);
    }
    setSelectedFiu('');
    setSelectedLinkedFip('');
    setUserActivity({});
    setEntityId('');
  };

  const timeLineRender = () => {
    return Object.keys(userActivity).length !== 0 ? (
      Object.keys(userActivity).map((userActivities: any, index: number) => (
        <Timeline.Item className='timeline' key={index} label={userActivities}>
          {userActivity[userActivities].map((activity: any, index1: number) => (
            <>
              <Card
                key={index1}
                style={{
                  width: '90%',
                  marginTop: 16,
                  border: '2px solid rgba(16, 84, 118, 0.2)',
                  borderRadius: 10,
                }}
              >
                <Skeleton loading={isLoading} avatar={true}>
                  <Card.Meta
                    title={activityTitle(
                      activity.activityType,
                      activity.activityJson,
                      t
                    )}
                    description={activityDescription(
                      activity.activityType,
                      activity.activityJson
                    )}
                  />
                </Skeleton>
              </Card>
              <Typography.Text type='secondary'>
                {moment(new Date(activity.time)).format('hh:mm a')}
              </Typography.Text>
            </>
          ))}
        </Timeline.Item>
      ))
    ) : (
      <Empty
        style={{ marginTop: 10 }}
        description={
          <Typography.Text className='consentErrorMsg'>
            {t('No')} {t('recent activities')}
          </Typography.Text>
        }
      />
    );
  };

  const renderEmptyTimeline = () => (
    <Timeline.Item style={{ paddingBottom: '0px' }} dot={<></>} />
  );

  const onPageAndPageSizeChange = (pageArgs: number, pageSizeArgs: number) => {
    setPage(pageArgs);
    setPageSize(pageSizeArgs);
    setUserActivity({});
  };

  const onPageAndPageSizeChangeRoot = (
    pageArgs: number,
    pageSizeArgs: number
  ) => {
    setPageRoot(pageArgs);
    setPageSizeRoot(pageSizeArgs);
    setUserActivity({});
    setEntityId('');
  };

  useEffect(() => {
    if (activeTab === '2' && selectedFiu) {
      getAllConsentsByFIU(selectedFiu, pageRoot - 1, pageSizeRoot)
        .then((res) => res.data)
        .then((res) => {
          setConsents(res.content);
          setTotalCountRoot(res.totalElements);
        });
    } else if (activeTab === '3' && selectedLinkedFip) {
      getAllAccountsByFIP(selectedLinkedFip, pageRoot - 1, pageSizeRoot)
        .then((res) => res.data)
        .then((res) => {
          setAccounts(res.content);
          setTotalCountRoot(res.totalElements);
        });
    }
  }, [activeTab, pageRoot, pageSizeRoot, selectedFiu, selectedLinkedFip]);

  const onChangeFIUSelection = (key: string | string[]) => {
    if (key !== undefined) {
      setEntityId('');
      setPageRoot(1);
      setSelectedFiu(fiu[parseInt(key[0], 10)].id);
    }
  };

  const onConsentSelectionChange = (key: string | string[]) => {
    if (key !== undefined) {
      setPage(1);
      setEntityId(consents[parseInt(key[0], 10)].consentHandle);
    }
  };

  const collapseFIUHeader = (fiuArgs: any) => {
    return (
      <div style={{ display: 'flex' }}>
        <Image
          className='fipLogo'
          src={logoBaseUrl + fiuArgs.id + '.png'}
          fallback={default_bank}
          preview={false}
        />

        <div className='cardContent'>
          <Typography.Text className='cardContentFIP'>
            {fiuArgs.name}
          </Typography.Text>
        </div>
      </div>
    );
  };

  const colorRenderer = (consent: any) => {
    if (consent.consentStatusType === ConsentStatusType.REJECTED) {
      return 'error';
    } else if (consent.consentStatusType === ConsentStatusType.EXPIRED) {
      return 'default';
    } else if (consent.consentStatusType === ConsentStatusType.PAUSED) {
      return 'default';
    } else if (consent.consentStatusType === ConsentStatusType.ACTIVE) {
      return 'success';
    } else if (consent.consentStatusType === ConsentStatusType.CREATED) {
      return 'processing';
    } else {
      return 'warning';
    }
  };

  const iconRenderer = (consent: any) => {
    if (consent.consentStatusType === ConsentStatusType.REJECTED) {
      return <CloseCircleOutlined />;
    } else if (consent.consentStatusType === ConsentStatusType.EXPIRED) {
      return <ClockCircleOutlined />;
    } else if (consent.consentStatusType === ConsentStatusType.PAUSED) {
      return <PauseCircleOutlined />;
    } else if (consent.consentStatusType === ConsentStatusType.ACTIVE) {
      return <CheckCircleOutlined />;
    } else if (consent.consentStatusType === ConsentStatusType.CREATED) {
      return <IssuesCloseOutlined />;
    } else {
      return <MinusCircleOutlined />;
    }
  };

  const colorRendererAccount = (account: any) => {
    if (account.accountStatusType === 'DELINKED') {
      return 'error';
    } else {
      return 'success';
    }
  };

  const iconRendererAccount = (account: any) => {
    if (account.accountStatusType === 'LINKED') {
      return <LinkOutlined />;
    } else {
      return <DeleteOutlined />;
    }
  };

  const collapseConsentHeader = (consent: any) => {
    return (
      <div style={{ display: 'flex' }}>
        <div className='typography'>
          <div className='fiuName'>
            <Statistic
              title='Purpose'
              value={`${consent.purpose.code} - ${consent.purpose.text}`}
              prefix={<AimOutlined />}
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '40%',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              height: '100%',
            }}
          >
            <div>
              <Tag
                className='tag'
                icon={iconRenderer(consent)}
                color={colorRenderer(consent)}
              >
                {consent.consentStatusType}
              </Tag>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Statistic
                className='statastics'
                title={t('Created on')}
                value={moment(consent.createdOn).format(
                  'MMMM Do YYYY, h:mm:ss'
                )}
              />
              <TimeFromNow date={consent.updatedOn} title={t('Updated')} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderList = () => {
    return (activeTab === '2' && fiu.length !== 0) ||
      (activeTab === '3' && linkedFip.length !== 0) ? (
      <Collapse
        expandIconPosition='end'
        accordion={true}
        onChange={
          activeTab === '2' ? onChangeFIUSelection : onChangeFIPSelection
        }
        destroyInactivePanel={true}
        className='fiuOuterCollapse'
      >
        {(activeTab === '2' ? fiu : linkedFip).map((f, index) => (
          <Collapse.Panel
            style={{ marginTop: 5 }}
            header={collapseFIUHeader(f)}
            key={index}
          >
            {(activeTab === '2' && consents.length !== 0) ||
            (activeTab === '3' && accounts.length !== 0) ? (
              <>
                <Pagination
                  size='small'
                  total={totalCountRoot}
                  defaultCurrent={6}
                  current={pageRoot}
                  pageSize={pageSizeRoot}
                  defaultPageSize={20}
                  showSizeChanger={true}
                  className='paginationAudit'
                  showTotal={showTotal}
                  responsive={true}
                  onChange={onPageAndPageSizeChangeRoot}
                />
                <Collapse
                  expandIconPosition='end'
                  style={{ border: 'none', background: 'none' }}
                  accordion={true}
                  onChange={
                    activeTab === '2'
                      ? onConsentSelectionChange
                      : onAccountSelectionChange
                  }
                  className='innerCollapse'
                >
                  {(activeTab === '2' ? consents : accounts).map(
                    (c, index1) => (
                      <Collapse.Panel
                        style={{
                          borderRadius: 12,
                          border: '1px solid #d9d9d9',
                          marginTop: 5,
                        }}
                        header={
                          activeTab === '2'
                            ? collapseConsentHeader(c)
                            : collapseAccountHeader(c)
                        }
                        key={index1}
                      >
                        <Pagination
                          size='small'
                          total={totalCount}
                          defaultCurrent={6}
                          current={page}
                          pageSize={pageSize}
                          defaultPageSize={20}
                          showSizeChanger={true}
                          className='paginationAudit'
                          showTotal={showTotal}
                          responsive={true}
                          onChange={onPageAndPageSizeChange}
                        />
                        {Object.keys(userActivity).length !== 0 && entityId ? (
                          timeLineRender()
                        ) : (
                          <></>
                        )}
                        {renderEmptyTimeline()}
                      </Collapse.Panel>
                    )
                  )}
                </Collapse>
              </>
            ) : (
              <Empty
                style={{ marginTop: 10 }}
                description={
                  <Typography.Text className='consentErrorMsg'>
                    {t('No')} {t('recent activities')}
                  </Typography.Text>
                }
              />
            )}
          </Collapse.Panel>
        ))}
      </Collapse>
    ) : (
      <Empty
        style={{ marginTop: 10 }}
        description={
          <Typography.Text className='consentErrorMsg'>
            {t('No')} {t('recent activities')}
          </Typography.Text>
        }
      />
    );
  };

  const collapseAccountHeader = (account: any) => {
    return (
      <div style={{ display: 'flex' }}>
        <div className='typography'>
          <div className='fiuName'>
            <div>
              <Statistic
                title='Account number'
                value={account.maskedAccNumber}
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <Statistic title='Account Type' value={account.accountType} />
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '40%',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              height: '100%',
            }}
          >
            <div>
              <Tag
                className='tag'
                icon={iconRendererAccount(account)}
                color={colorRendererAccount(account)}
              >
                {account.accountStatusType}
              </Tag>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Statistic
                className='statastics'
                title={t('Created on')}
                value={moment(account.createdOn).format(
                  'MMMM Do YYYY, h:mm:ss'
                )}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const onChangeFIPSelection = (key: string | string[]) => {
    if (key !== undefined) {
      setPageRoot(1);
      setEntityId('');
      setSelectedLinkedFip(linkedFip[parseInt(key[0], 10)].id);
    }
  };

  const onAccountSelectionChange = (key: string | string[]) => {
    if (key !== undefined) {
      setPage(1);
      setEntityId(accounts[parseInt(key[0], 10)].linkRefNumber);
    }
  };

  const showTotal = (total: number, range: number[]) => (
    <Statistic
      className='paginationStats'
      title={`${range[0]}-${range[1]} of ${total} items`}
    />
  );

  const options = [
    { label: 'ASC', value: 'ASC' },
    { label: 'DESC', value: 'DESC' },
  ];

  const OnRadioFilterChange = ({ target: { value } }: RadioChangeEvent) => {
    setPage(1);
    setSort(value);
  };

  const appltFilter = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Typography.Text>Apply filters</Typography.Text>
        <Radio.Group
          options={options}
          onChange={OnRadioFilterChange}
          value={sort}
        />
      </div>
    );
  };

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography.Title
        level={2}
        style={{
          marginLeft: 'auto',
          color: 'var(--ant-primary-color)',
          width: '95%',
          marginRight: 'auto',
          marginTop: 15,
        }}
      >
        History
      </Typography.Title>

      <Card className='timelineTabs'>
        <Tabs
          onChange={retrieveAuditBasedOnKey}
          animated={true}
          defaultActiveKey='1'
          destroyInactiveTabPane={true}
        >
          <Tabs.TabPane tab='All notifications' key='1'>
            <Timeline className='timelineRoot' mode='left'>
              {userActivity && Object.keys(userActivity).length !== 0 ? (
                <div
                  style={{ display: 'flex', justifyContent: 'space-around' }}
                >
                  <Pagination
                    size='small'
                    total={totalCount}
                    defaultCurrent={6}
                    current={page}
                    pageSize={pageSize}
                    defaultPageSize={20}
                    showSizeChanger={true}
                    className='paginationAudit'
                    showTotal={showTotal}
                    responsive={true}
                    onChange={onPageAndPageSizeChange}
                  />
                  <Popconfirm
                    icon={<></>}
                    title={appltFilter()}
                    cancelButtonProps={{ hidden: true }}
                    okButtonProps={{ hidden: true }}
                  >
                    <FilterOutlined className='filter' />
                  </Popconfirm>
                </div>
              ) : (
                <></>
              )}
              {timeLineRender()}
              {renderEmptyTimeline()}
            </Timeline>
          </Tabs.TabPane>
          <Tabs.TabPane tab='Consent(s)' key='2'>
            <Timeline className='timelineRoot' mode='left'>
              {renderList()}
            </Timeline>
          </Tabs.TabPane>
          <Tabs.TabPane tab='Account(s)' key='3'>
            <Timeline className='timelineRoot' mode='left'>
              {renderList()}
            </Timeline>
          </Tabs.TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  isLoading: state.loader.searchLoader,
});

export default connect(mapStateToProps)(UserActivity);
